/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ModalWidget", ["WidgetGroupBase", "WidgetFactory"], function (context, cls) {
  /**
   * Modal widget.
   * @class ModalWidget
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   */
  cls.ModalWidget = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.ModalWidget.prototype */ {
      __name: "ModalWidget",
      /**
       * header element
       * @type {HTMLElement}
       */
      _header: null,
      /**
       * header hidden state
       * @type {boolean}
       */
      _headerHidden: false,
      /**
       * footer element
       * @type {HTMLElement}
       */
      _footer: null,
      /**
       * title element
       * @type {HTMLElement}
       */
      _title: null,
      /**
       * dialog pane element
       * @type {HTMLElement}
       */
      _dialogPane: null,

      /**
       * header actions host element
       * @type {HTMLElement}
       */
      _actionsHost: null,
      /**
       * close button element
       * @type {HTMLElement}
       */
      _closeButton: null,

      /**
       * resize handler
       */
      _systemResizeHandler: null,

      /**
       * Elements made `inert` by the modal focus trap, to be restored on release.
       * @type {?HTMLElement[]}
       */
      _modalInertedElements: null,

      /**
       * flag to set first fixed size
       * @type {boolean}
       */
      _hasInitialContainment: true,
      /**
       * is movable
       * @type {boolean}
       */
      _canMove: false,
      /**
       * has it moved
       * @type {boolean}
       */
      _hasBeenMoved: false,
      /**
       * internal computing information
       * @type {?{current:{x:number, y:number}, reference:{x:number, y:number}, minDelta:{x:number, y:number}, maxDelta:{x:number, y:number}, drag:{x:number, y:number}}}
       */
      _movingPositions: null,
      /**
       * is it moving
       * @type {boolean}
       */
      _isMoving: false,
      /**
       * move drag handle element
       * @type {HTMLElement}
       */
      _movableDragHandle: null,
      /**
       * has been positioned by VM
       * @type {boolean}
       */
      _positionedByVm: false,
      /** @type {Object} **/
      _initialVmPosition: null,

      /**
       * is it sizable
       * @type {boolean}
       */
      _canSize: false,
      /**
       * has it resized
       * @type {boolean}
       */
      _hasBeenSized: false,
      /**
       * internal computing information
       * @type {?{current:{x:number, y:number}, size:{x:number, y:number}, min:{x:number, y:number}, max:{x:number, y:number}, contentMin:{x:number, y:number}, decoration:{x:number, y:number}, drag:{x:number, y:number}}}
       */
      _sizingPositions: null,
      /**
       * is it resizing
       * @type {boolean}
       */
      _isSizing: false,
      /**
       * resize drag handle element
       * @type {HTMLElement}
       */
      _sizableDragHandle: null,
      /**
       * resize drag indicator element
       * @type {HTMLElement}
       */
      _resizerIcon: null,
      /**
       * is modal closable. Yes by default.
       * @type {boolean}
       */
      _isClosable: true,

      /**
       * Directly hide on click
       * @type {boolean}
       */
      _directlyHide: false,

      /**
       * Hide on overlay click
       * @type {boolean}
       */
      _hideOnClickOut: false,

      /**
       * Should the modal Widget reset its transformations at start
       * @type {boolean}
       */
      _forceDefaultSettings: false,

      constructor: function (opts) {
        // Build the storedSettingsKey for this modal
        this._storedSettingsKey = opts.storedSettingsKey || {
          appId: -1,
          formName: "_genero_settings_modal_",
          windowName: "_genero_settings_"
        };
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._header = this._element.getElementsByClassName("mt-dialog-header")[0];
        this._header.classList.remove("hidden");
        this._footer = this._element.getElementsByClassName("mt-dialog-footer")[0];
        // Empty in the template; hide it (display:none) until setFooter() fills it.
        cls.DOMElementHelper.toggleClass(this._footer, "hidden", true);
        this._title = this._header.getElementsByClassName("mt-dialog-title")[0];
        this._actionsHost = this._header.getElementsByClassName("mt-dialog-actions")[0];
        this._dialogPane = this._element.querySelector(".mt-dialog-pane");
        this._closeButton = this._actionsHost.getElementsByClassName("close")[0];
        this._resizerIcon = this._element.getElementsByClassName("mt-resizer-icon")[0];
        // Assigned here so setHeader can point aria-labelledby at it (only when
        // the title is non-empty, else it would override the static aria-label).
        this._title.id = `w_${this.getUniqueIdentifier()}_title`;
        this._initMovable();
        this._initSizable();
        this.restoreStoredDimension();
        this.restorePosition();
        this.setMovable(true);
      },

      resetLayout: function () {
        this._hasInitialContainment = true;
        this._hasBeenMoved = false;
        this._hasBeenSized = false;
        this._dialogPane.style.top = "auto";
        this._dialogPane.style.right = "auto";
        this._dialogPane.style.bottom = "auto";
        this._dialogPane.style.left = "auto";
        this._dialogPane.style.width = "auto";
        this._dialogPane.style.height = "auto";
        this._dialogPane.classList.remove("moved");
        this._dialogPane.classList.remove("sized");
        this._resetMovable();
        this._resetSizable();
        this.getLayoutEngine().reset(true);
        this.getLayoutInformation().reset(true);
      },
      /**
       * set first fixed size
       * @private
       */
      _removeInitialContainment: function () {
        if (this._hasInitialContainment) {
          this._hasInitialContainment = false;
          const containerRect = this.getElement().getBoundingClientRect(),
            dialogRect = this._dialogPane.getBoundingClientRect();
          this._setAsMoved(
            this.isReversed() ? containerRect.right - dialogRect.right : dialogRect.left - containerRect.left,
            dialogRect.top - containerRect.top
          );
          this._dialogPane.classList.remove("initial");
        }
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        if (domEvent.target === null) {
          return true;
        } // just ignore qa click
        if (domEvent.target.classList.contains("close") || domEvent.target.parentElement.classList.contains("close")) {
          if (this._directlyHide) {
            this.hide();
          }
          this.emit(context.constants.widgetEvents.close);
        }

        // backdrop click
        if (domEvent.target === this._element) {
          if (this._hideOnClickOut) {
            this.hide();
          }
          this.emit(context.constants.widgetEvents.modalOut);
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      manageMouseDblClick: function (domEvent) {
        // Double click on resizer restore default size
        if (cls.DOMElementHelper.isElementOrChildOf(domEvent.target, this._resizer)) {
          this.resetDimension();
        }
        // Double click on header restore default position
        if (cls.DOMElementHelper.isElementOrChildOf(domEvent.target, this._header)) {
          this.resetPosition();
        }
        return true;
      },

      /**
       * Make the modal window movable
       * @param {boolean} movable will it be movable
       */
      setMovable: function (movable) {
        this._canMove = movable;
        this.toggleClass("movable", movable);
      },

      /**
       * Start the movable process
       * @private
       */
      _initMovable: function () {
        this._resetMovable();
        this._movableDragHandle = this._element.getElementsByClassName("movable_firefox_placekeeper")[0];
        cls.DOMElementHelper.on(this._header, "pointerdown.MovableModalWidget", this._onMovablePointerDown.bind(this));
      },

      _resetMovable: function () {
        this._movingPositions = {
          reference: {
            x: 0,
            y: 0
          },
          current: {
            x: 0,
            y: 0
          },
          minDelta: {
            x: 0,
            y: 0
          },
          maxDelta: {
            x: 0,
            y: 0
          },
          drag: {
            x: 0,
            y: 0
          }
        };
      },

      /**
       * Move Modal at given position
       * @param {number} x - horizontal position
       * @param {number} y - vertical position
       */
      setPosition: function (x, y) {
        // this comes from external class (mostly 4ST position:field/center) : don't save in storedSettings
        this._positionedByVm = true;
        this._initialVmPosition = {
          x,
          y
        };
        this._setAsMoved(x, y);
        if (!this.isInViewport()) {
          this.resetDimension();
        }
      },

      /**
       * move at given position
       * @param {number} x x position
       * @param {number} y y position
       * @private
       */
      _setAsMoved: function (x, y) {
        this._dialogPane.classList.add("moved");
        this._removeInitialContainment();
        this._movingPositions.reference.x = x;
        this._movingPositions.reference.y = y;
        const themeMarginRatio = context.ThemeService.getValue("ui-margin-ratio");
        const maxX = `${Math.max(themeMarginRatio, this._movingPositions.reference.x)}px`;
        this._dialogPane.style.left = this.isReversed() ? "" : maxX;

        this._dialogPane.style.right = !this.isReversed() ? "" : maxX;

        this._dialogPane.style.top = `${Math.max(themeMarginRatio, this._movingPositions.reference.y)}px`;

        this._hasBeenMoved = true;
      },

      _resetAsMoved: function () {
        this._dialogPane.classList.remove("moved");
        this._movingPositions.reference.x = 0;
        this._movingPositions.reference.y = 0;
        this._dialogPane.style.top = "auto";
        this._dialogPane.style.right = "auto";
        this._dialogPane.style.bottom = "auto";
        this._dialogPane.style.left = "auto";
        this._hasBeenMoved = true;
      },

      /**
       * Ensure the modal is fully visible in viewPort, will reset position if not
       */
      ensureInViewPort: function () {
        if (!this.isInViewport() && !this._isSizing) {
          this.resetDimension();
          this.resetPosition();
        }
      },

      /**
       * Save Layout measured Info for sizing / positioning modal
       * @param {classes.Size} info - the measured size to store
       */
      setMeasuredInfo: function (info) {
        this._measuredInfo = info;
      },

      /**
       * handle on pointer down for move
       * @param {PointerEvent} evt the event
       * @private
       */
      _onMovablePointerDown: function (evt) {
        if (!this._canMove) {
          cls.EventHelper.preventCancelableDefault(evt);
          return;
        }

        if (evt.target.closest(".close")) {
          return;
        }

        this.addClasses("moving");
        this._header.classList.add("dragging");

        const containerRect = this.getElement().getBoundingClientRect(),
          dialogRect = this._dialogPane.getBoundingClientRect();
        if (!this._hasBeenMoved) {
          this._setAsMoved(
            this.isReversed() ? containerRect.right - dialogRect.right : dialogRect.left - containerRect.left,
            dialogRect.top - containerRect.top
          );
        }

        const themeMarginRatio = context.ThemeService.getValue("ui-margin-ratio");
        this._movingPositions.minDelta.x =
          (this.isReversed() ? dialogRect.right - containerRect.right : containerRect.left - dialogRect.left) + themeMarginRatio;
        this._movingPositions.minDelta.y = containerRect.top - dialogRect.top + themeMarginRatio;

        this._movingPositions.maxDelta.x =
          (this.isReversed()
            ? dialogRect.right - containerRect.right + containerRect.width - dialogRect.width
            : containerRect.left - dialogRect.left + containerRect.width - dialogRect.width) - themeMarginRatio;

        this._movingPositions.maxDelta.y = containerRect.height + containerRect.top - dialogRect.height - dialogRect.top - themeMarginRatio;
        this._isMoving = true;
        this._movingPositions.current.x = evt.pageX;
        this._movingPositions.current.y = evt.pageY;
        this._movingPositions.drag.x = evt.pageX;
        this._movingPositions.drag.y = evt.pageY;

        this._onMovePointerMove = this._onMovablePointerMove.bind(this);
        this._onMovePointerUp = this._onMovablePointerUp.bind(this);
        document.addEventListener("pointermove", this._onMovePointerMove, { passive: false });
        document.addEventListener("pointerup", this._onMovePointerUp, { passive: false });
      },

      /**
       * handle on pointer up for move
       * @param {PointerEvent} evt the event
       * @private
       */
      _onMovablePointerUp: function (evt) {
        this.removeClass("moving");
        this._header.classList.remove("dragging");
        const deltaX = (this._movingPositions.current.x - this._movingPositions.drag.x) * (this.isReversed() ? -1 : 1);
        const deltaY = this._movingPositions.current.y - this._movingPositions.drag.y;
        if (deltaX * deltaX + deltaY * deltaY > 16 * 16) {
          const themeMarginRatio = context.ThemeService.getValue("ui-margin-ratio");
          this._movingPositions.reference.x = Math.max(
            themeMarginRatio,
            this._movingPositions.reference.x + Math.min(Math.max(deltaX, this._movingPositions.minDelta.x), this._movingPositions.maxDelta.x)
          );
          this._movingPositions.reference.y = Math.max(
            themeMarginRatio,
            this._movingPositions.reference.y + Math.min(Math.max(deltaY, this._movingPositions.minDelta.y), this._movingPositions.maxDelta.y)
          );
        }
        this._isMoving = false;
        document.removeEventListener("pointermove", this._onMovePointerMove);
        document.removeEventListener("pointerup", this._onMovePointerUp);
        this.savePosition();
      },

      /**
       * handle on pointer move for move
       * @param {PointerEvent} evt the event
       * @private
       */
      _onMovablePointerMove: function (evt) {
        if (this._isMoving) {
          this._movingPositions.current.x = evt.pageX;
          this._movingPositions.current.y = evt.pageY;

          const deltaX = (this._movingPositions.current.x - this._movingPositions.drag.x) * (this.isReversed() ? -1 : 1);
          const deltaY = this._movingPositions.current.y - this._movingPositions.drag.y;
          if (deltaX * deltaX + deltaY * deltaY > 100) {
            this._updateMovablePosition(
              Math.min(Math.max(deltaX, this._movingPositions.minDelta.x), this._movingPositions.maxDelta.x),
              Math.min(Math.max(deltaY, this._movingPositions.minDelta.y), this._movingPositions.maxDelta.y)
            );
          }
        }
      },

      /**
       * update visual position when moving
       * @param {number} deltaX x position delta
       * @param {number} deltaY y position delta
       * @private
       */
      _updateMovablePosition: function (deltaX, deltaY) {
        this._dialogPane.style.left = this.isReversed() ? "" : "" + `${Math.max(this._getPadding(), this._movingPositions.reference.x + deltaX)}px`;
        this._dialogPane.style.right = !this.isReversed() ? "" : "" + `${Math.max(this._getPadding(), this._movingPositions.reference.x + deltaX)}px`;
        this._dialogPane.style.top = `${Math.max(this._getPadding(), this._movingPositions.reference.y + deltaY)}px`;
      },

      /**
       * Make the modal window resizable
       * @param {boolean} sizable will it be sizable
       */
      setSizable: function (sizable) {
        this._canSize = sizable;
        this.toggleClass("sizable", sizable);
      },
      /**
       * init sizable data
       * @private
       */
      _initSizable: function () {
        this._resetSizable();
        this._resizer = this._element.querySelector(".mt-dialog-resizer");
        this._sizableDragHandle = this._element.getElementsByClassName("sizable_firefox_placekeeper")[0];
        cls.DOMElementHelper.on(this._resizer, "pointerdown.SizableModalWidget", this._onSizablePointerDown.bind(this));
      },

      _resetSizable: function () {
        this._sizingPositions = {
          size: {
            x: 0,
            y: 0
          },
          reference: {
            x: 0,
            y: 0
          },
          current: {
            x: 0,
            y: 0
          },
          min: {
            x: 0,
            y: 0
          },
          contentMin: {
            x: 0,
            y: 0
          },
          max: {
            x: 0,
            y: 0
          },
          drag: {
            x: 0,
            y: 0
          },
          decoration: {
            x: 0,
            y: 0
          }
        };
      },

      /**
       * handle on pointer down for resize
       * @param {PointerEvent} evt the event
       * @private
       */
      _onSizablePointerDown: function (evt) {
        if (this._canSize) {
          this.addClasses("sizing");
          const containerRect = this.getElement().getBoundingClientRect(),
            dialogRect = this._dialogPane.getBoundingClientRect(),
            contentRect = (this._element.querySelector(".gbc_WindowContent") || this._containerElement).getBoundingClientRect();
          if (!this._hasBeenMoved) {
            this._setAsMoved(dialogRect.left - containerRect.left, dialogRect.top - containerRect.top);
          }
          if (!this._hasBeenSized) {
            this._dialogPane.classList.add("sized");
            this._removeInitialContainment();
            this._sizingPositions.min.x = dialogRect.width;
            this._sizingPositions.min.y = dialogRect.height;
            this._hasBeenSized = true;
          }
          this._sizingPositions.decoration.x = dialogRect.width - contentRect.width;
          this._sizingPositions.decoration.y = dialogRect.height - contentRect.height;

          // min value between initial rendered size and min size defined by layout engine
          this._sizingPositions.min.x = Math.min(
            this._sizingPositions.contentMin.x
              ? this._sizingPositions.contentMin.x + this._sizingPositions.decoration.x + window.scrollBarSize
              : dialogRect.width,
            this._sizingPositions.min.x
          );
          this._sizingPositions.min.y = Math.min(
            this._sizingPositions.contentMin.y
              ? this._sizingPositions.contentMin.y + this._sizingPositions.decoration.y + window.scrollBarSize
              : dialogRect.height,
            this._sizingPositions.min.y
          );
          this._sizingPositions.size.x = this._sizingPositions.reference.x = dialogRect.width;
          this._sizingPositions.size.y = this._sizingPositions.reference.y = dialogRect.height;

          this._sizingPositions.max.x =
            (this.isReversed()
              ? containerRect.width + dialogRect.right - containerRect.right
              : containerRect.width - dialogRect.left + containerRect.left) -
            context.ThemeService.getValue("ui-margin-ratio") * 8;
          this._sizingPositions.max.y = containerRect.height - dialogRect.top + containerRect.top - this._getPadding();
          this._isSizing = true;
          this._sizingPositions.current.x = evt.pageX;
          this._sizingPositions.current.y = evt.pageY;
          this._sizingPositions.drag.x = evt.pageX;
          this._sizingPositions.drag.y = evt.pageY;

          this._onSizePointerMove = this._onSizablePointerMove.bind(this);
          this._onSizePointerUp = this._onSizablePointerUp.bind(this);

          document.addEventListener("pointermove", this._onSizePointerMove, { passive: false });
          document.addEventListener("pointerup", this._onSizePointerUp, { passive: false });

          evt.preventDefault();
        } else {
          cls.EventHelper.preventCancelableDefault(evt);
        }
        return false;
      },

      /**
       * handle on pointer up for resize
       * @param {PointerEvent} evt the event
       * @private
       */
      _onSizablePointerUp: function (_evt) {
        this.removeClass("sizing");
        this._resizer.classList.remove("dragging");

        const deltaX =
          this._sizingPositions.reference.x + (this._sizingPositions.current.x - this._sizingPositions.drag.x) * (this.isReversed() ? -1 : 1);
        const deltaY = this._sizingPositions.reference.y + this._sizingPositions.current.y - this._sizingPositions.drag.y;
        this._sizingPositions.reference.x = Math.min(Math.max(deltaX, this._sizingPositions.min.x), this._sizingPositions.max.x);
        this._sizingPositions.reference.y = Math.min(Math.max(deltaY, this._sizingPositions.min.y), this._sizingPositions.max.y);
        this._sizingPositions.size.x = this._sizingPositions.reference.x;
        this._sizingPositions.size.y = this._sizingPositions.reference.y;
        this._isSizing = false;

        document.removeEventListener("pointermove", this._onSizePointerMove);
        document.removeEventListener("pointerup", this._onSizePointerUp);

        this.emit(context.constants.widgetEvents.modalResize);
        this.saveDimension();
        this.savePosition(); // save position as we want to put it where it was
      },

      /**
       * handle on pointer move for resize
       * @param {PointerEvent} evt the event
       * @private
       */
      _onSizablePointerMove: function (evt) {
        if (this._isSizing) {
          this._sizingPositions.current.x = evt.pageX;
          this._sizingPositions.current.y = evt.pageY;

          evt.preventDefault();

          const deltaX =
            this._sizingPositions.reference.x + (this._sizingPositions.current.x - this._sizingPositions.drag.x) * (this.isReversed() ? -1 : 1);
          const deltaY = this._sizingPositions.reference.y + this._sizingPositions.current.y - this._sizingPositions.drag.y;
          this._sizingPositions.size.x = Math.min(Math.max(deltaX, this._sizingPositions.min.x), this._sizingPositions.max.x);
          this._sizingPositions.size.y = Math.min(Math.max(deltaY, this._sizingPositions.min.y), this._sizingPositions.max.y);
          this._updateSizablePosition(this._sizingPositions.size.x, this._sizingPositions.size.y);
        }
      },

      /**
       * update visual size when resizing
       * @param {number} deltaX x position delta
       * @param {number} deltaY y position delta
       * @private
       */
      _updateSizablePosition: function (deltaX, deltaY) {
        this._dialogPane.style.width = `${deltaX}px`;
        this._dialogPane.style.height = `${deltaY}px`;
        this.emit(context.constants.widgetEvents.modalResize);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._image) {
          this._image.destroy();
          this._image = null;
        }
        if (this._systemResizeHandler) {
          this._systemResizeHandler();
          this._systemResizeHandler = null;
        }
        // Ensure the inert background is removed even when the modal is closed via destroy()
        // without a prior hide() (e.g. gbc.alert). Runs before $super.destroy →
        // _deactivateSystemWidget restores focus to a no-longer-inert opener.
        this._releaseModalFocusTrap();
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      _initAsSystemWidget: function () {
        $super._initAsSystemWidget.call(this);
        if (!this._systemResizeHandler) {
          this._systemResizeHandler = context.HostService.onScreenResize(this.resizeHandler.bind(this));
        }
      },

      /**
       * @inheritDoc
       */
      manageKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this._isSystemWidget) {
          // Case of system widget
          if (keyString === "esc" && this._isClosable) {
            this.hide();
            if (!this.isDestroyed()) {
              this.destroy();
            }
            return true;
          }
        }
        return false;
      },

      /**
       * fired when resizing
       */
      resizeHandler: function () {
        if (this._element) {
          this.toggleClass("left-realign", this._dialogPane.offsetWidth > this._element.offsetWidth);
          this.toggleClass("top-realign", this._dialogPane.offsetHeight > this._element.offsetHeight);
        }
      },

      /**
       * Save current modal Positions to StoredSettings
       * Won't store it if positioned by VM or system modal, or 4ST forceDefaultSettings is true
       */
      savePosition: function () {
        if (!this._positionedByVm && !this._isSystemWidget && !this._forceDefaultSettings) {
          const { appId, formName, windowName } = this._storedSettingsKey;
          gbc.StoredSettingsService.saveModalSettings(appId, formName, windowName, {
            position: this._movingPositions
          });
          gbc.LogService.ui.log("ModalWidget - Moved -> saved Position in stored settings");
        }
      },

      /**
       * Cancel all manual positioning of the modal to initial
       */
      resetPosition: function () {
        if (!this._hasBeenMoved) {
          return;
        }
        gbc.LogService.ui.log("ModalWidget - restore original Position");
        const { appId, formName, windowName } = this._storedSettingsKey;
        gbc.StoredSettingsService.saveModalSettings(appId, formName, windowName, {
          position: {}
        });
        this._hasBeenMoved = false;

        // Take care of the reset position for modal positioned by VM
        if (this._positionedByVm) {
          this._setAsMoved(this._initialVmPosition.x, this._initialVmPosition.y);
          return;
        }
        this._dialogPane.style.top = "auto";
        this._dialogPane.style.right = "auto";
        this._dialogPane.style.bottom = "auto";
        this._dialogPane.style.left = "auto";
        this._dialogPane.classList.remove("moved");
        this._resetMovable();
        if (this.getLayoutEngine()) {
          this.getLayoutEngine().reset(true);
          this.getLayoutInformation().reset(true);
        }
        this.emit(context.constants.widgetEvents.modalResize);
      },

      /**
       * Restore modal position from StoredSettings
       */
      restorePosition: function () {
        const { appId, formName, windowName } = this._storedSettingsKey;
        const storedMovingPositions = gbc.StoredSettingsService.loadModalSettings(appId, formName, windowName, "position");

        if (storedMovingPositions && Object.keys(storedMovingPositions).length > 0) {
          gbc.LogService.ui.log("ModalWidget - restore Position from stored settings");
          const { x, y } = storedMovingPositions.reference;
          this._setAsMoved(x, y); // restore position
        }
      },

      /**
       * Save current modal Dimensions to StoredSettings
       * Won't store it if system modal, or 4ST forceDefaultSettings is true
       */
      saveDimension: function () {
        if (!this._isSystemWidget && !this._forceDefaultSettings) {
          const { appId, formName, windowName } = this._storedSettingsKey;
          gbc.StoredSettingsService.saveModalSettings(appId, formName, windowName, {
            dimension: this._sizingPositions
          });
          gbc.LogService.ui.log("ModalWidget - Resized -> saved Dimensions in stored settings");
        }
      },

      /**
       * Cancel all dimensioning of the modal to initial
       */
      resetDimension: function () {
        if (!this._hasBeenSized) {
          return;
        }
        gbc.LogService.ui.log("ModalWidget - restore original Dimensions");
        const { appId, formName, windowName } = this._storedSettingsKey;
        gbc.StoredSettingsService.saveModalSettings(appId, formName, windowName, {
          dimension: {}
        });
        this._hasBeenSized = false;
        this._dialogPane.style.width = "auto";
        this._dialogPane.style.height = "auto";
        this._dialogPane.classList.remove("sized");
        this._resetSizable();
        if (this.getLayoutEngine()) {
          this.getLayoutEngine().reset(true);
          this.getLayoutInformation().reset(true);
        }
        this.emit(context.constants.widgetEvents.modalResize);
      },

      /**
       * Restore modal Dimensions from StoredSettings
       */
      restoreStoredDimension: function () {
        const { appId, formName, windowName } = this._storedSettingsKey;
        const storedSizingDimension = gbc.StoredSettingsService.loadModalSettings(appId, formName, windowName, "dimension");

        if (storedSizingDimension && Object.keys(storedSizingDimension).length > 0) {
          gbc.LogService.ui.log("ModalWidget - restore Dimensions from stored settings");
          this._sizingPositions = storedSizingDimension;
          // Cannot restore dimension if the modal isn't in the viewport
          if (!this.isInViewport()) {
            this.resetDimension(); //reset initial dimension
            return;
          }
          this._dialogPane.style.width = this._sizingPositions.size.x + "px";
          this._dialogPane.style.height = this._sizingPositions.size.y + "px";
          this._hasBeenSized = true;
          this.emit(context.constants.widgetEvents.modalResize);
        }
      },

      /**
       * Check if modal is fully in the viewport
       * @return {boolean} - true if in viewport, false otherwise
       */
      isInViewport: function () {
        const { innerWidth, innerHeight } = window;

        const rect = this._dialogPane.getBoundingClientRect();

        const inViewPort = {
          x: rect.x + rect.width < innerWidth,
          y: rect.y + rect.height < innerHeight
        };

        return inViewPort.x && inViewPort.y;
      },

      /**
       * @inheritDoc
       */
      setBackgroundColor: function (color) {
        this._backgroundColor = color;
        this.setStyle(".mt-dialog-pane", {
          "background-color": color && !this._ignoreBackgroundColor ? color : null
        });
      },

      /**
       * set the modal header
       * @param {string|HTMLElement} header the header
       */
      setHeader: function (header) {
        if (cls.ObjectHelper.isString(header)) {
          cls.DOMElementHelper.toggleClass(this._header, "hidden", header.length === 0 || this._headerHidden);
          this._title.textContent = header;
        } else {
          cls.DOMElementHelper.empty(this._title);
          if (header) {
            this._title.appendChild(header);
          }
        }
        // Label by the title only when it holds non-blank content; an empty
        // labelledby target would otherwise override the static aria-label.
        this.setAriaAttribute("labelledby", this._title.textContent.trim().length > 0 ? this._title.id : null);
      },

      /**
       * Returns header element
       * @returns {HTMLElement}
       */
      getHeader: function () {
        return this._header;
      },

      /**
       * set header raw hidden state
       * @param {boolean} headerHidden is header hidden
       */
      setHeaderHidden: function (headerHidden) {
        if (headerHidden !== this._headerHidden) {
          this._headerHidden = Boolean(headerHidden);
          cls.DOMElementHelper.toggleClass(this._header, "hidden", this._title.textContent.length === 0 || this._headerHidden);
        }
      },
      /**
       * Set the image of the modal widget
       * @param {string} image the image src
       */
      setImage: function (image) {
        if (image && image !== "") {
          if (!this._image) {
            this._image = cls.WidgetFactory.createWidget("Image", this.getBuildParameters());
            this._title.parentNode.insertBefore(this._image.getElement(), this._title);
          }
          this._image.setSrc(image);
        }
      },

      /**
       * set the modal footer
       * @param {string|HTMLElement} footer - the footer
       */
      setFooter: function (footer) {
        if (cls.ObjectHelper.isString(footer)) {
          this._footer.textContent = footer;
          cls.DOMElementHelper.toggleClass(this._footer, "hidden", footer.length === 0);
        } else {
          cls.DOMElementHelper.empty(this._footer);
          if (footer) {
            this._footer.appendChild(footer);
          }
          cls.DOMElementHelper.toggleClass(this._footer, "hidden", !footer);
        }
      },

      /**
       * Return footer element
       * @returns {HTMLElement}
       */
      getFooter: function () {
        return this._footer;
      },

      /**
       * set the modal content
       * @param {string|HTMLElement} content - the content
       */
      setContent: function (content) {
        if (cls.ObjectHelper.isString(content)) {
          this._containerElement.textContent = content;
        } else if (this._containerElement.children.length !== 1 || this._containerElement.children[0] !== content) {
          // Setting this._containerElement.innerHTML = "" can cause unwanted cleaning under IE11. In our case content.innerHTML gets cleaned (GBC-727). Prefer cleaning by looping on children
          cls.DOMElementHelper.empty(this._containerElement);
          if (content) {
            this._containerElement.appendChild(content);
          }
        }
      },

      /**
       * Set the modal closable
       * @param {boolean} closable -  true if the dialog is closable, false otherwise
       * @param {boolean} directlyHide -
       * @param {boolean} hideOnClickOut - true if dialg should be hidden when user click outside of modal
       */
      setClosable: function (closable, directlyHide, hideOnClickOut) {
        cls.DOMElementHelper.toggleClasses(this._closeButton, !closable, "hidden");
        this._isClosable = Boolean(closable);
        this._directlyHide = Boolean(directlyHide);
        this._hideOnClickOut = Boolean(hideOnClickOut);
      },

      /**
       * is the modal closable
       * @returns {boolean} true if the dialog is closable, false otherwise
       */
      isClosable: function () {
        return this._isClosable;
      },
      /**
       * show the modal
       */
      show: function () {
        if (this._element) {
          this.addClasses("displayed");
        }
        this._hidden = false;

        // Trap focus + make the rest of the DOM inert for blocking modals (system widgets AND
        // 4GL modal windows), only in accessibility mode. Applied before _activateSystemWidget:
        // the trap captures the opener first (before inerting blurs it), then
        // _activateSystemWidget's idempotent save keeps that opener.
        if (this._shouldTrapFocus()) {
          this._applyModalFocusTrap();
        }

        if (this._isSystemWidget) {
          this._activateSystemWidget();
          this.resizeHandler();
        }
      },
      /**
       * hide the modal
       */
      hide: function () {
        if (this._element && this.isVisible()) {
          this.removeClass("displayed");
          this._hidden = true;
          this.emit(context.constants.widgetEvents.close);
        }
        // Remove the inert background BEFORE _deactivateSystemWidget restores focus, otherwise
        // the opener (e.g. the settings button) is still inert and cannot regain focus.
        if (this._shouldTrapFocus()) {
          this._releaseModalFocusTrap();
        }
        if (this._isSystemWidget) {
          this._deactivateSystemWidget();
        }
      },

      /**
       * Whether this modal should make the content behind it `inert` (WCAG 2.4.3).
       * True for blocking modals: system widgets (settings, gbc.alert, WindowMessage, file
       * picker) and 4GL modal windows (`gbc_ModalWindowDialog`). Transient popups (popup/menu
       * dropdowns) are excluded.
       * @returns {boolean}
       * @private
       */
      _shouldTrapFocus: function () {
        return Boolean(this._isSystemWidget || this._element?.classList.contains("gbc_ModalWindowDialog"));
      },

      /**
       * Apply the modal focus trap. Only active in keyboard accessibility mode; in
       * normal mode GBC modals keep their existing (non-inert) behaviour.
       * - System widgets are true client-level modals: the whole UI behind is made `inert`
       *   (`_inertWholeBackground`).
       * - 4GL modal windows inert the `gbc_WindowMainContainer` of every other window behind them
       *   (`_inertBackgroundWindows`). Gating to accessibility mode is what preserves the normal
       *   GBC modal model outside it (app chrome, window toolbar and parent-window menu actions
       *   stay reachable in normal mode).
       * For system widgets the opener is captured here **before** inerting, because inerting its
       * subtree synchronously blurs it to `<body>`; otherwise `_activateSystemWidget` would save
       * `<body>` and `_deactivateSystemWidget` could not restore focus to the opener on close.
       * DOM focus inside the modal is moved/restored by `_activateSystemWidget` /
       * `_deactivateSystemWidget` (system) or the VM (4GL). Idempotent (guarded by
       * `_modalInertedElements`).
       * @private
       */
      _applyModalFocusTrap: function () {
        if (this._modalInertedElements) {
          return;
        }
        // Only trap when keyboard accessibility mode is on: in normal mode GBC modals keep
        // their existing (non-inert) behaviour.
        if (!context.KeyboardService.isAccessibilityModeEnabled()) {
          return;
        }
        // Capture the opener BEFORE inerting (inerting its subtree blurs it to <body>), so the
        // idempotent save in _activateSystemWidget keeps the real opener and _deactivateSystemWidget
        // can restore focus to it on close.
        if (this._isSystemWidget && !this._preSystemFocusedElement) {
          this._preSystemFocusedElement = document.activeElement;
        }
        this._modalInertedElements = this._isSystemWidget ? this._inertWholeBackground() : this._inertBackgroundWindows();
      },

      /**
       * Release the modal focus trap (remove the `inert` marks). Idempotent.
       * @private
       */
      _releaseModalFocusTrap: function () {
        if (this._modalInertedElements) {
          for (const element of this._modalInertedElements) {
            element.removeAttribute("inert");
          }
          this._modalInertedElements = null;
        }
      },

      /**
       * Re-evaluate this modal's focus trap against the current accessibility mode. Called when the
       * mode is toggled live so an already-open modal gains the `inert` background when the mode is
       * turned on and drops it when turned off (both underlying calls are idempotent).
       */
      refreshModalFocusTrap: function () {
        if (!this._shouldTrapFocus() || this._hidden) {
          return;
        }
        if (context.KeyboardService.isAccessibilityModeEnabled()) {
          this._applyModalFocusTrap();
        } else {
          this._releaseModalFocusTrap();
        }
      },

      /**
       * (System widgets) Make everything behind the modal `inert`. The modal shares its ancestors
       * with the background all the way up to `<body>`, so there is no single ancestor that wraps
       * "the background but not the modal". Instead we walk from the modal up to `<body>` and mark
       * each OFF-PATH sibling at every level. `inert` propagates to a marked element's whole
       * subtree, so each sibling is marked ONCE (not per descendant) and the modal's own branch
       * (never its own sibling) stays interactive. Returns the marked set so it can be released.
       * The host dropdown container is exempted: the modal's own combos render their dropdowns
       * there, and an inert dropdown ignores mouse hover and clicks. The inert background cannot
       * open any dropdown, so nothing behind the modal becomes reachable through it.
       * @returns {HTMLElement[]} the elements this call marked inert
       * @private
       */
      _inertWholeBackground: function () {
        const inerted = [];
        const skipTags = { SCRIPT: 1, STYLE: 1, LINK: 1, META: 1, NOSCRIPT: 1, TEMPLATE: 1 };
        const dropDownContainer = context.HostService?.getDropDownContainer();
        let node = this._element;
        while (node?.parentElement) {
          const parent = node.parentElement;
          for (const sibling of Array.from(parent.children)) {
            if (
              sibling !== node &&
              sibling !== dropDownContainer &&
              sibling.nodeType === 1 &&
              !skipTags[sibling.tagName] &&
              !sibling.hasAttribute("inert")
            ) {
              sibling.setAttribute("inert", "");
              inerted.push(sibling);
            }
          }
          if (parent === document.body) {
            break;
          }
          node = parent;
        }
        return inerted;
      },

      /**
       * (4GL modal windows) Mark the main container (`gbc_WindowMainContainer`) of every OTHER
       * window in this application `inert`, so keyboard focus cannot reach a window behind the
       * modal. Only applied in accessibility mode (see `_applyModalFocusTrap`), so the normal 4GL
       * modal behaviour (chrome / toolbar / parent-window menus reachable) is unchanged.
       * The modal's own window is skipped.
       * @returns {HTMLElement[]} the elements this call marked inert
       * @private
       */
      _inertBackgroundWindows: function () {
        const inerted = [];
        const root = this._element.closest(".gbc_UserInterfaceWidget") || document.body;
        for (const container of Array.from(root.querySelectorAll(".gbc_WindowMainContainer"))) {
          if (!this._element.contains(container) && !container.contains(this._element) && !container.hasAttribute("inert")) {
            container.setAttribute("inert", "");
            inerted.push(container);
          }
        }
        return inerted;
      },

      /**
       * register a hook when modal is closed
       * @param {Hook} hook the hook to fire
       * @param {boolean=} once - if true, will only fire once
       * @return {HandleRegistration} a handle registration to free the hook
       */
      onClose: function (hook, once) {
        this.when(context.constants.widgetEvents.close, hook, once);
      },

      /**
       * Ignore stored Settings (for position and size)
       * @param {Boolean} forceDefaultSettings
       */
      setForceDefaultSettings: function (forceDefaultSettings) {
        this._forceDefaultSettings = forceDefaultSettings;
      },

      /**
       * @returns The computed padding of the modal
       */
      _getPadding: function () {
        return context.ThemeService.getValue("ui-margin-ratio") * 8;
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Modal", cls.ModalWidget);
});
